import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import AppShell from "@/components/layout/AppShell";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Plus, ChefHat, Minus, TrendingUp } from "lucide-react";
import { toast } from "sonner";

export default function Recipes() {
    const { activeHotelId } = useAuth();
    const [recipes, setRecipes] = useState([]);
    const [menu, setMenu] = useState([]);
    const [inventory, setInventory] = useState([]);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ menu_item_id: "", yield_servings: 1, ingredients: [], prep_time_min: 15, instructions: "" });
    const [newIng, setNewIng] = useState({ inventory_item_id: "", quantity: 100, unit: "g" });

    const load = async () => {
        if (!activeHotelId) return;
        try {
            const [r, m, i] = await Promise.all([
                api.get(`/recipes?hotel_id=${activeHotelId}`),
                api.get(`/restaurant/menu?hotel_id=${activeHotelId}`),
                api.get(`/inventory/items?hotel_id=${activeHotelId}&category=Kitchen`),
            ]);
            setRecipes(r.data); setMenu(m.data); setInventory(i.data);
        } catch (e) { toast.error(formatApiError(e)); }
    };
    useEffect(() => { load(); }, [activeHotelId]);

    const addIng = () => {
        const item = inventory.find(x => x.id === newIng.inventory_item_id);
        if (!item) return;
        setForm({ ...form, ingredients: [...form.ingredients, { ...newIng, name: item.name, quantity: Number(newIng.quantity) }] });
        setNewIng({ inventory_item_id: "", quantity: 100, unit: "g" });
    };

    const removeIng = (idx) => setForm({ ...form, ingredients: form.ingredients.filter((_, i) => i !== idx) });

    const submit = async () => {
        try {
            await api.post("/recipes", { ...form, hotel_id: activeHotelId, yield_servings: Number(form.yield_servings), prep_time_min: Number(form.prep_time_min) });
            toast.success("Recipe saved");
            setOpen(false); setForm({ menu_item_id: "", yield_servings: 1, ingredients: [], prep_time_min: 15, instructions: "" }); load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const del = async (id) => {
        if (!confirm("Delete this recipe?")) return;
        try { await api.delete(`/recipes/${id}`); toast.success("Deleted"); load(); }
        catch (e) { toast.error(formatApiError(e)); }
    };

    return (
        <AppShell title="Recipe Management" subtitle={`${recipes.length} recipes · auto-deducts inventory on order billing`}>
            <div className="flex justify-end mb-4">
                <Dialog open={open} onOpenChange={setOpen}>
                    <DialogTrigger asChild><Button className="bg-[#0A1C3E] rounded-sm" data-testid="recipe-add-btn"><Plus className="w-4 h-4 mr-1" /> Recipe</Button></DialogTrigger>
                    <DialogContent className="max-w-2xl">
                        <DialogHeader><DialogTitle>New Recipe</DialogTitle></DialogHeader>
                        <div className="space-y-3">
                            <div><Label className="text-xs uppercase tracking-wider">Menu Item</Label>
                                <Select value={form.menu_item_id} onValueChange={v => setForm({ ...form, menu_item_id: v })}>
                                    <SelectTrigger data-testid="recipe-menu"><SelectValue placeholder="Choose menu item" /></SelectTrigger>
                                    <SelectContent>{menu.map(m => <SelectItem key={m.id} value={m.id}>{m.name} · ₹{m.price}</SelectItem>)}</SelectContent>
                                </Select></div>
                            <div className="grid grid-cols-2 gap-3">
                                <div><Label className="text-xs uppercase tracking-wider">Yield (servings)</Label><Input type="number" value={form.yield_servings} onChange={e => setForm({ ...form, yield_servings: e.target.value })} /></div>
                                <div><Label className="text-xs uppercase tracking-wider">Prep time (min)</Label><Input type="number" value={form.prep_time_min} onChange={e => setForm({ ...form, prep_time_min: e.target.value })} /></div>
                            </div>
                            <div className="border border-slate-200 rounded p-3 bg-slate-50">
                                <div className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">Ingredients</div>
                                {form.ingredients.map((ing, idx) => (
                                    <div key={idx} className="flex justify-between text-sm py-1 border-b border-slate-100">
                                        <span>{ing.quantity} {ing.unit} · {ing.name}</span>
                                        <button onClick={() => removeIng(idx)} className="text-red-500"><Minus className="w-3 h-3" /></button>
                                    </div>
                                ))}
                                <div className="grid grid-cols-5 gap-2 mt-2">
                                    <Select value={newIng.inventory_item_id} onValueChange={v => setNewIng({ ...newIng, inventory_item_id: v })}>
                                        <SelectTrigger className="col-span-2" data-testid="recipe-ing-item"><SelectValue placeholder="Item" /></SelectTrigger>
                                        <SelectContent>{inventory.map(i => <SelectItem key={i.id} value={i.id}>{i.name} ({i.unit})</SelectItem>)}</SelectContent>
                                    </Select>
                                    <Input type="number" placeholder="Qty" value={newIng.quantity} onChange={e => setNewIng({ ...newIng, quantity: e.target.value })} />
                                    <Input placeholder="Unit" value={newIng.unit} onChange={e => setNewIng({ ...newIng, unit: e.target.value })} />
                                    <Button onClick={addIng} disabled={!newIng.inventory_item_id} variant="outline" size="sm" data-testid="recipe-ing-add">Add</Button>
                                </div>
                            </div>
                        </div>
                        <DialogFooter><Button onClick={submit} disabled={!form.menu_item_id || !form.ingredients.length} className="bg-[#0A1C3E]" data-testid="recipe-submit">Save Recipe</Button></DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {recipes.map(r => (
                    <Card key={r.id} className="p-4 border border-slate-200 rounded-md shadow-none" data-testid={`recipe-${r.id}`}>
                        <div className="flex justify-between items-start">
                            <ChefHat className="w-5 h-5 text-[#C5B08A]" />
                            <button onClick={() => del(r.id)} className="text-slate-400 hover:text-red-600 text-xs">Delete</button>
                        </div>
                        <div className="mt-2 font-semibold text-slate-900">{r.name}</div>
                        <div className="text-xs text-slate-500">Yield: {r.yield_servings} · Prep: {r.prep_time_min}min</div>
                        <div className="mt-3 pt-3 border-t border-slate-100 space-y-2">
                            <div className="flex justify-between text-sm"><span className="text-slate-500">Selling Price</span><span className="font-mono">₹{r.menu_item_price}</span></div>
                            <div className="flex justify-between text-sm"><span className="text-slate-500">Cost / serving</span><span className="font-mono text-red-600">₹{r.cost_per_serving}</span></div>
                            <div className="flex justify-between text-sm pt-2 border-t"><span className="font-semibold">Margin</span><span className={`font-mono font-bold ${r.profit_margin > 50 ? "text-emerald-700" : "text-orange-600"}`}>{r.profit_margin}%</span></div>
                        </div>
                        <div className="mt-3 text-xs text-slate-500">
                            {r.ingredients?.length} ingredient{r.ingredients?.length !== 1 ? "s" : ""}
                        </div>
                    </Card>
                ))}
                {recipes.length === 0 && <div className="col-span-full text-center text-slate-500 py-10">No recipes yet. Add a recipe to auto-deduct ingredients on order billing.</div>}
            </div>
        </AppShell>
    );
}
